import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  Table,
  Button,
  Row,
  Col,
  Form,
  InputGroup,
  Pagination,
  Badge,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaPlus, FaEdit, FaTrash, FaSearch } from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import { QUANTIDADEPORPAGINA } from '~/constant';
import {
  listarTipos,
  excluirTipo,
} from '~/Services/TipoAtendimentoService';
import ModalTipo from './ModalTipo';
import ModalListaSubTipos from './ModalListaSubTipos';

const linkSubTipos = {
  cursor: 'pointer',
  color: '#0747a6',
  textDecoration: 'underline',
  userSelect: 'none',
};

const TipoAtendimento = () => {
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filtroCodigo, setFiltroCodigo] = useState('');
  const [filtroDescricao, setFiltroDescricao] = useState('');
  const [paginaTipos, setPaginaTipos] = useState(1);

  const [modalTipo, setModalTipo] = useState({ aberto: false, modo: null, dados: null });
  const [modalSubTipos, setModalSubTipos] = useState({ aberto: false, tipo: null });
  const [confirmacaoTipo, setConfirmacaoTipo] = useState({ show: false, item: null });

  useEffect(() => {
    carregarTipos();
  }, []);

  useEffect(() => {
    setPaginaTipos(1);
  }, [filtroCodigo, filtroDescricao]);

  const carregarTipos = async () => {
    setLoading(true);
    try {
      const res = await listarTipos({});
      setTipos(res.data || []);
    } catch {
      toast.error('Erro ao carregar tipos de atendimento.');
    } finally {
      setLoading(false);
    }
  };

  const tiposFiltrados = tipos.filter((t) => {
    const matchCodigo = filtroCodigo
      ? String(t.codigo).includes(filtroCodigo.trim())
      : true;
    const matchDescricao = filtroDescricao
      ? (t.descricao || '').toLowerCase().includes(filtroDescricao.toLowerCase().trim())
      : true;
    return matchCodigo && matchDescricao;
  });

  const totalTipos = tiposFiltrados.length;
  const tiposPaginados = tiposFiltrados.slice(
    (paginaTipos - 1) * QUANTIDADEPORPAGINA,
    paginaTipos * QUANTIDADEPORPAGINA,
  );

  const renderizarPaginacao = (total, pagina, setPagina) => {
    const totalPags = Math.ceil(total / QUANTIDADEPORPAGINA);
    if (totalPags <= 1) return null;
    const inicio = (pagina - 1) * QUANTIDADEPORPAGINA + 1;
    const fim = Math.min(pagina * QUANTIDADEPORPAGINA, total);

    let start = Math.max(1, pagina - 2);
    const end = Math.min(totalPags, start + 4);
    if (end - start < 4) start = Math.max(1, end - 4);

    const itens = [];
    if (start > 1) itens.push(<Pagination.Ellipsis key="e1" disabled />);
    for (let p = start; p <= end; p++) {
      itens.push(
        <Pagination.Item key={p} active={p === pagina} onClick={() => setPagina(p)}>
          {p}
        </Pagination.Item>,
      );
    }
    if (end < totalPags) itens.push(<Pagination.Ellipsis key="e2" disabled />);

    return (
      <div className="d-flex justify-content-between align-items-center px-3 py-2 flex-wrap">
        <small className="text-muted">
          Mostrando {inicio} até {fim} de {total} registros
        </small>
        <Pagination size="sm" className="mb-0">
          <Pagination.First disabled={pagina === 1} onClick={() => setPagina(1)} />
          <Pagination.Prev disabled={pagina === 1} onClick={() => setPagina(pagina - 1)} />
          {itens}
          <Pagination.Next disabled={pagina === totalPags} onClick={() => setPagina(pagina + 1)} />
          <Pagination.Last disabled={pagina === totalPags} onClick={() => setPagina(totalPags)} />
        </Pagination>
      </div>
    );
  };

  const handleNovoTipo = () => setModalTipo({ aberto: true, modo: 'criar', dados: null });
  const handleEditarTipo = (item) => setModalTipo({ aberto: true, modo: 'editar', dados: item });
  const handleExcluirTipo = (item) => setConfirmacaoTipo({ show: true, item });
  const handleAbrirSubTipos = (tipo) => setModalSubTipos({ aberto: true, tipo });

  const handleConfirmarExclusaoTipo = async (confirmado) => {
    const item = confirmacaoTipo.item;
    setConfirmacaoTipo({ show: false, item: null });
    if (!confirmado) return;
    try {
      await excluirTipo({ codigo: item.codigo });
      toast.success('Tipo de Atendimento excluído com sucesso.');
      carregarTipos();
    } catch (err) {
      const mensagem = err && err.message ? err.message : 'Erro ao excluir.';
      toast.error(mensagem);
    }
  };

  const handleSucessoTipo = () => {
    setModalTipo({ aberto: false, modo: null, dados: null });
    carregarTipos();
  };

  if (loading) return <Loading />;

  return (
    <Container fluid className="px-3 py-3">
      <Header title="Tipo de Atendimento" />

      <Card className="shadow-sm mb-3">
        <Card.Body className="py-2">
          <Row className="align-items-center">
            <Col xs={12} md={3}>
              <InputGroup size="sm">
                <InputGroup.Prepend>
                  <InputGroup.Text><FaSearch /></InputGroup.Text>
                </InputGroup.Prepend>
                <Form.Control
                  placeholder="Código"
                  value={filtroCodigo}
                  onChange={(e) => setFiltroCodigo(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col xs={12} md={4} className="mt-2 mt-md-0">
              <InputGroup size="sm">
                <InputGroup.Prepend>
                  <InputGroup.Text><FaSearch /></InputGroup.Text>
                </InputGroup.Prepend>
                <Form.Control
                  placeholder="Descrição"
                  value={filtroDescricao}
                  onChange={(e) => setFiltroDescricao(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col className="text-right mt-2 mt-md-0">
              <Button size="sm" variant="primary" onClick={handleNovoTipo}>
                <FaPlus className="mr-1" /> Novo Tipo
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="shadow-sm mb-3">
        <Card.Header
          className="d-flex justify-content-between align-items-center py-2"
          style={{ backgroundColor: '#0747a6', color: '#fff' }}
        >
          <strong>
            Tipos de Atendimento
            <span className="ml-2" style={{ fontSize: '0.85rem', opacity: 0.8 }}>
              ({tiposFiltrados.length} {tiposFiltrados.length === 1 ? 'registro' : 'registros'})
            </span>
          </strong>
        </Card.Header>
        <Card.Body className="p-0">
          {tiposFiltrados.length === 0 ? (
            <p className="text-muted text-center my-4">Nenhum tipo cadastrado.</p>
          ) : (
            <Table striped bordered hover size="sm" className="mb-0">
              <thead>
                <tr>
                  <th style={{ width: '90px' }}>Código</th>
                  <th>Descrição</th>
                  <th style={{ width: '160px' }}>SubTipos</th>
                  <th style={{ width: '90px', textAlign: 'center' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {tiposPaginados.map((tipo) => (
                  <tr key={tipo.codigo}>
                    <td>{tipo.codigo}</td>
                    <td>{tipo.descricao}</td>
                    <td>
                      <span
                        style={linkSubTipos}
                        onClick={() => handleAbrirSubTipos(tipo)}
                        title="Ver subtipos"
                      >
                        <Badge variant="secondary">
                          {tipo.qtd_subtipos != null ? tipo.qtd_subtipos : 0}
                        </Badge>
                      </span>
                    </td>
                    <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="mr-1"
                        title="Editar"
                        onClick={() => handleEditarTipo(tipo)}
                      >
                        <FaEdit />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        title="Excluir"
                        onClick={() => handleExcluirTipo(tipo)}
                      >
                        <FaTrash />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          {renderizarPaginacao(totalTipos, paginaTipos, setPaginaTipos)}
        </Card.Body>
      </Card>

      <ModalTipo
        show={modalTipo.aberto}
        onHide={() => setModalTipo({ aberto: false, modo: null, dados: null })}
        modo={modalTipo.modo}
        dados={modalTipo.dados}
        onSucesso={handleSucessoTipo}
      />

      <ModalListaSubTipos
        show={modalSubTipos.aberto}
        onHide={() => setModalSubTipos((prev) => ({ ...prev, aberto: false }))}
        tipo={modalSubTipos.tipo}
        onMutacao={carregarTipos}
      />

      <ModalConfirmacao
        show={confirmacaoTipo.show}
        title="Confirmar Exclusão"
        message={
          confirmacaoTipo.item
            ? `Deseja excluir o tipo "${confirmacaoTipo.item.descricao}" (Código ${confirmacaoTipo.item.codigo})?`
            : ''
        }
        onClose={handleConfirmarExclusaoTipo}
      />
    </Container>
  );
};

export default TipoAtendimento;
